// Pomocniki integracji radia z loggerem: strojenie z klawiatury, pasma,
// podpowiedz znaku ze spotu na czestotliwosci radia, raport stanu radia USB na serwer.
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { freqToBandJs } from './utils';

const API = process.env.REACT_APP_API_URL || '';

// Krok strojenia (Hz) zalezny od emisji; z Shiftem x10
export function tuneStepHz(mode) {
  const m = String(mode || '').toUpperCase();
  if (m === 'FM') return 12500;
  if (m === 'AM') return 1000;
  if (m === 'SSB' || m === 'USB' || m === 'LSB') return 500;
  return 100;                    // CW, RTTY, PSK, cyfrowe
}
export function stepFreq(mhz, mode, dir, big) {
  const step = tuneStepHz(mode) * (big ? 10 : 1);
  const hz = Math.round(Number(mhz) * 1e6);
  // do najblizszej "okraglej" wartosci w kierunku strojenia
  const next = dir > 0 ? Math.floor(hz / step) * step + step : Math.ceil(hz / step) * step - step;
  return Math.max(0.1, next / 1e6);
}

// Czestotliwosc "domowa" pasma: [CW, fonia, cyfrowe (FT8)]
const BAND_HOME = [
  ['160m', 1.830, 1.850, 1.840], ['80m', 3.530, 3.750, 3.573], ['60m', 5.3515, 5.3565, 5.357], ['40m', 7.020, 7.150, 7.074],
  ['30m', 10.115, 10.115, 10.136], ['20m', 14.030, 14.200, 14.074], ['17m', 18.080, 18.130, 18.100], ['15m', 21.030, 21.250, 21.074],
  ['12m', 24.900, 24.950, 24.915], ['10m', 28.030, 28.500, 28.074], ['6m', 50.090, 50.150, 50.313], ['2m', 144.050, 144.300, 144.174],
  ['70cm', 432.050, 432.200, 432.174],
];
const FM_HOME = { '10m': 29.600, '6m': 51.510, '2m': 145.500, '70cm': 433.500 };
// Sasiednie pasmo (dir = +1 wyzsze, -1 nizsze) -> czestotliwosc domowa dla emisji
export function neighbourBand(mhz, mode, dir) {
  const m = String(mode || '').toUpperCase();
  const band = freqToBandJs(Number(mhz));
  let i = BAND_HOME.findIndex(b => b[0] === band);
  if (i < 0) i = BAND_HOME.findIndex(b => b[1] > Number(mhz)) - (dir > 0 ? 1 : 0);
  const row = BAND_HOME[Math.max(0, Math.min(BAND_HOME.length - 1, i + dir))];
  if (m === 'FM' && FM_HOME[row[0]]) return { band: row[0], freq: FM_HOME[row[0]] };
  const col = m === 'CW' ? 1 : ['SSB', 'USB', 'LSB', 'AM', 'FM'].includes(m) ? 2 : 3;
  return { band: row[0], freq: row[col] };
}

// Kolko myszy nad AKTYWNYM polem czestotliwosci (React rejestruje wheel jako pasywny --
// preventDefault wymaga wlasnego sluchacza)
export function useFreqWheel(ref, onStep) {
  const cb = useRef(onStep);
  cb.current = onStep;
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (document.activeElement !== el || !e.deltaY) return;
      e.preventDefault();
      cb.current(e.deltaY < 0 ? 1 : -1, e.shiftKey);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });
}

// Spot klastra najblizej czestotliwosci radia (do podpowiedzi znaku). Pure -- testowane.
export function nearestSpot(spots, mhz, mode) {
  const f = Number(mhz);
  if (!f || !spots?.length) return null;
  const narrow = !['SSB', 'USB', 'LSB', 'AM', 'FM'].includes(String(mode || '').toUpperCase());
  const tol = narrow ? 0.0004 : 0.0015;          // 400 Hz dla CW/cyfrowych, 1,5 kHz dla fonii
  let best = null;
  for (const s of spots) {
    const d = Math.abs(Number(s.freq) - f);
    if (d <= tol && (!best || d < best.d)) best = { d, spot: s };
  }
  return best ? best.spot : null;
}

// Spoty pasma, na ktorym jest radio (odswiezane co 30 s) -> spot pod aktualna czestotliwoscia
export function useSpotAtRadio(mhz, mode, enabled) {
  const [spots, setSpots] = useState([]);
  const band = enabled && mhz ? freqToBandJs(Number(mhz)) : '';
  useEffect(() => {
    if (!band) { setSpots([]); return undefined; }
    let alive = true;
    const load = () => axios.get(`${API}/api/cluster/bandmap`, { params: { band } })
      .then(r => { if (alive) setSpots(r.data.spots || []); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [band]);
  return band ? nearestSpot(spots, mhz, mode) : null;
}

// Radio podlaczone przez USB w przegladarce raportuje stan na serwer tak jak agent --
// widza go inne widoki i urzadzenia (mapa pasma, telefon). Najwyzej raz na 5 s + co 20 s podtrzymanie.
export function useRadioStatusPush(cat) {
  const lastSent = useRef(0);
  const web = cat.source === 'webserial';
  useEffect(() => {
    if (!web) return undefined;
    const send = () => {
      lastSent.current = Date.now();
      axios.post(`${API}/api/cat/status`, { frequency: cat.frequency, mode: cat.mode, source: 'webserial' }).catch(() => {});
    };
    const first = setTimeout(send, Math.max(0, 5000 - (Date.now() - lastSent.current)));
    const beat = setInterval(send, 20000);
    return () => { clearTimeout(first); clearInterval(beat); };
  }, [web, cat.frequency, cat.mode]);
  useEffect(() => {
    if (!web) return undefined;
    return () => { axios.post(`${API}/api/cat/status`, { disconnect: true }).catch(() => {}); };
  }, [web]);
}
